// Five-knob corpus settings form — PRD 08 §10.1 + Slice C3.
//
// Reads + writes corpora/<corpusId>/config.json via the TanStack Query
// hooks in ./api.ts. Optimistic updates so the form reflects the new
// value the instant the user clicks; the dashboard-side live preview pane
// per PRD 08 § "Out of v1 narrow cut" is deferred.

import { useEffect, useState } from "react";
import {
  CORPUS_CONFIG_DEFAULTS,
  type AccentSlug,
  type CorpusConfig,
  type RailPosition,
  type RegisterDefault,
  type ThemeMode,
  type TypeScale,
} from "../../data/types";
import { AccentSwatchPicker } from "./AccentSwatchPicker";
import { useCorpusConfig, usePutCorpusConfig } from "./api";
import "./corpus-settings.css";

export interface CorpusSettingsFormProps {
  corpusId: string;
}

const THEMES: ThemeMode[] = ["light", "dark", "auto"];
const SCALES: TypeScale[] = ["small", "medium", "large"];
const REGISTERS: RegisterDefault[] = ["plain", "precise"];
const RAILS: RailPosition[] = ["left", "right", "split"];

export function CorpusSettingsForm({ corpusId }: CorpusSettingsFormProps) {
  const { data, isPending, isError, error } = useCorpusConfig(corpusId);
  const mutation = usePutCorpusConfig(corpusId);

  // Local form state — initialized from the server's value, optimistically
  // synced as the user makes changes.
  const [draft, setDraft] = useState<CorpusConfig>(
    data ?? CORPUS_CONFIG_DEFAULTS,
  );

  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  if (isPending) return <div className="maisight-loading">Loading settings…</div>;
  if (isError)
    return (
      <div className="maisight-error" role="alert">
        Could not load settings: {error?.message}
      </div>
    );

  const patch = (next: Partial<CorpusConfig>): void => {
    const merged = { ...draft, ...next };
    setDraft(merged);
    mutation.mutate(merged);
  };

  return (
    <form
      className="maisight-corpus-settings"
      aria-label="Corpus settings"
      onSubmit={(e) => e.preventDefault()}
    >
      <fieldset className="maisight-settings-group">
        <legend>Theme</legend>
        {THEMES.map((t) => (
          <label key={t}>
            <input
              type="radio"
              name="theme"
              value={t}
              checked={draft.theme === t}
              onChange={() => patch({ theme: t })}
            />
            {t}
          </label>
        ))}
      </fieldset>

      <fieldset className="maisight-settings-group">
        <legend>Accent</legend>
        <AccentSwatchPicker
          value={draft.accent}
          onChange={(accent: AccentSlug) => patch({ accent })}
        />
      </fieldset>

      <fieldset className="maisight-settings-group">
        <legend>Type scale</legend>
        <div role="radiogroup" className="maisight-segmented">
          {SCALES.map((s) => (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={draft.type_scale === s}
              className={`maisight-segment${draft.type_scale === s ? " is-selected" : ""}`}
              onClick={() => patch({ type_scale: s })}
            >
              {s}
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="maisight-settings-group">
        <legend>Default register</legend>
        {REGISTERS.map((r) => (
          <label key={r}>
            <input
              type="radio"
              name="register"
              value={r}
              checked={draft.default_register === r}
              onChange={() => patch({ default_register: r })}
            />
            {r}
          </label>
        ))}
      </fieldset>

      <fieldset className="maisight-settings-group">
        <legend>Rail position</legend>
        <div role="radiogroup" className="maisight-segmented">
          {RAILS.map((r) => (
            <button
              key={r}
              type="button"
              role="radio"
              aria-checked={draft.rail_position === r}
              className={`maisight-segment${draft.rail_position === r ? " is-selected" : ""}`}
              onClick={() => patch({ rail_position: r })}
            >
              {r}
            </button>
          ))}
        </div>
      </fieldset>

      {mutation.isError ? (
        <div className="maisight-error" role="alert">
          Save failed: {mutation.error?.message}
        </div>
      ) : null}
      {mutation.isPending ? (
        <div className="maisight-settings-status">Saving…</div>
      ) : null}
    </form>
  );
}
